import { ProTable } from '@ant-design/pro-components'
import { Alert, Button, DatePicker, Select, Typography } from 'antd'
import dayjs from 'dayjs'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { getCreatedAtSearch } from '@/utils/list-query.ts'
import { formatAmount } from '@/utils/money.ts'
import { customerSendColumns } from './const/customer-send-columns.ts'
import { getPreviousMonthCreatedTime } from './search.ts'
import type { CustomerSendPreviewData } from './types.ts'

type Row = CustomerSendPreviewData['data'][number]
type Range = ReturnType<typeof getPreviousMonthCreatedTime>

export function CustomerSendPreview({
  data,
  customerName,
  range,
  pagination,
  accounts,
  botId,
  loading,
  error,
  onRangeChange,
  onPageChange,
  onBotChange,
  onAccountChange,
  onRefresh
}: {
  data?: CustomerSendPreviewData
  customerName: string
  range: Range
  pagination: { current: number; pageSize: number }
  accounts: Record<number, number | undefined>
  botId?: string
  loading: boolean
  error?: string
  onRangeChange: (range: Range) => void
  onPageChange: (current: number, pageSize: number) => void
  onBotChange: (botId: string) => void
  onAccountChange: (organizationId: number, accountId: number) => void
  onRefresh: () => void
}) {
  const bots = data?.bots ?? []
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="customer-send-created-from" className="font-medium">
          账单创建时间
        </label>
        <DatePicker.RangePicker
          id={{ start: 'customer-send-created-from', end: 'customer-send-created-to' }}
          className="w-full sm:w-72"
          allowClear={false}
          presets={RANGE_PRESETS}
          value={[dayjs(range.createdFrom).utcOffset(8), dayjs(range.createdTo).utcOffset(8)]}
          onChange={(dates) => {
            const next = getCreatedAtSearch(dates)
            if (next.createdFrom && next.createdTo) {
              onRangeChange({ createdFrom: next.createdFrom, createdTo: next.createdTo })
            }
          }}
        />
        <Button onClick={() => onRangeChange(getPreviousMonthCreatedTime())}>上个自然月</Button>
      </div>
      <p className="text-content-muted m-0 text-sm">
        仅合并已出账、未归入有效账单组且仍有待收余额的账单。按经营主体分别生成账单组，合成一份 PDF 后发送到客户绑定群。
      </p>
      {error && <Alert type="error" showIcon title={error} action={<Button onClick={onRefresh}>重试</Button>} />}
      {data && !error && (
        <div className="space-y-2 text-sm">
          <p className="m-0">
            客户：{customerName} · 本次 {data.billCount} 张账单
          </p>
          <p className="m-0">
            发送群：<Typography.Text copyable>{data.customer.wecomChatId}</Typography.Text>
          </p>
        </div>
      )}
      {!loading && !error && bots.length === 0 && (
        <Alert
          type="warning"
          showIcon
          title="暂无在线的客服推送通道"
          description="请检查 Hermes 客服网关心跳，通道就绪后重新预览。"
          action={<Button onClick={onRefresh}>刷新通道</Button>}
        />
      )}
      {bots.length > 0 && (
        <div className="space-y-2">
          <label htmlFor="customer-send-bot">发送机器人</label>
          <Select
            id="customer-send-bot"
            className="w-full"
            value={botId}
            onChange={onBotChange}
            options={bots.map((bot) => ({ value: bot.id, label: bot.name }))}
            placeholder="请选择在线客服机器人"
          />
        </div>
      )}
      <ProTable<Row>
        rowKey="id"
        size="small"
        columns={customerSendColumns}
        columnsState={{ persistenceKey: 'customer-send-preview-columns', persistenceType: 'localStorage' }}
        dataSource={error ? [] : (data?.data ?? [])}
        loading={loading}
        search={false}
        options={false}
        cardProps={false}
        toolBarRender={false}
        scroll={{ x: '56rem' }}
        locale={{ emptyText: '所选日期内没有符合条件的账单' }}
        pagination={{
          ...pagination,
          total: data?.total ?? 0,
          onChange: onPageChange,
          showTotal: (total) => `共 ${total} 张`
        }}
      />
      {!error &&
        data?.organizations.map((org) => (
          <div key={org.id} className="space-y-2">
            <label htmlFor={`send-account-${org.id}`}>
              {org.name} · {org.billCount} 张 · ¥{formatAmount(org.amount)} · 收款账户
            </label>
            <Select
              id={`send-account-${org.id}`}
              className="w-full"
              value={accounts[org.id]}
              placeholder="请选择收款账户"
              options={org.accounts.map((account) => ({ value: account.id, label: account.label }))}
              onChange={(accountId: number) => onAccountChange(org.id, accountId)}
            />
            {!org.accounts.length && <Alert type="warning" showIcon title="该主体暂无启用的收款账户，请先配置。" />}
          </div>
        ))}
    </div>
  )
}
